export interface ObjectId {
  $oid: string; // 24 hex characters
}

export interface Task {
  _id: ObjectId;
  shipmentId: string;
  shipmentName: string;
  title: string;
  description?: string;
  stage: TaskStage;
  status: TaskStatus;
  priority: TaskPriority;
  
  assignedTo?: ObjectId;
  assignedToName?: string;
  shipmentAssignedTo?: ObjectId;
  shipmentAssignedToName?: string;
  
  createdBy: ObjectId;
  createdByName: string;
  createdAt: Date;
  updatedAt: Date;
  completedAt?: Date;
  completedBy?: ObjectId;
  completedByName?: string;
  dueDate?: Date;
  tags?: string[];
  customTask: boolean;
  serviceAssignments: ServiceType[];
  isAllServices: boolean;
  estimatedDuration?: number;
  actualDuration?: number;
  notes?: string;
  attachments?: string[];
  /** For multi-car shipments: which specific car(s) this task applies to. Empty = all cars. */
  vehicleIds?: string[];
  /** For multi-car shipments: single carId this task is for. */
  vehicleId?: string;
}

export enum TaskStage {
  PLANNED = 'PLANNED',
  SCHEDULED = 'SCHEDULED',
  LOADED_ORIGIN = 'LOADED (ORIGIN)',
  ON_TERMINAL_POL = 'ON TERMINAL (POL)',
  ON_VESSEL = 'ON VESSEL',
  ON_TERMINAL_POD = 'ON TERMINAL (POD)',
  CUSTOMS_RELEASED = 'CUSTOMS RELEASED',
  LOADED_POD = 'LOADED (POD)',
  OUT_FOR_DELIVERY = 'OUT FOR DELIVERY',
  DELIVERED = 'DELIVERED',
  ON_HOLD = 'ON HOLD'
}

export enum TaskStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  ON_HOLD = 'on_hold'
}

export enum TaskPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent'
}

export enum ServiceType {
  PICKUP = 'pickup',
  OCEAN = 'ocean',
  CUSTOMS = 'customs',
  DELIVERY = 'delivery'
}

export interface TaskTemplate {
  _id: ObjectId;
  title: string;
  description?: string;
  stage: TaskStage;
  serviceAssignments: ServiceType[]; // Which services this task applies to (can be multiple)
  isAllServices: boolean; // Whether this task applies to all services (equivalent to "All Four" in CSV)
  priority: TaskPriority;
  estimatedDuration?: number;
  tags?: string[];
  isActive: boolean;
  // Date calculation for automatic due date assignment
  dueDateCalculation?: {
    type: 'etd' | 'eta' | 'fixed_days_from_etd' | 'fixed_days_from_eta' | 'pickup' | 'fixed_days_from_pickup' | 'pol_delivery' | 'fixed_days_from_pol_delivery' | 'pod_pickup' | 'fixed_days_from_pod_pickup' | 'delivery' | 'fixed_days_from_delivery' | 'created' | 'fixed_days_from_created'; // How to calculate the due date
    daysOffset?: number; // Number of days before/after ETD or ETA (-5 means 5 days before, 5 means 5 days after)
  };
  orderIndex?: number; // Order in which tasks should appear when creating for shipments
  createdAt: Date;
  updatedAt: Date;
}

export interface TaskAssignment {
  _id: ObjectId;
  taskId: ObjectId;
  assignedTo: ObjectId;
  assignedByName: string;
  assignedAt: Date;
  assignedBy: ObjectId;
  notes?: string;
}

export interface TaskNotification {
  _id: ObjectId;
  taskId: ObjectId;
  shipmentId: string;
  shipmentName: string;
  taskTitle: string;
  type: 'task_created' | 'task_assigned' | 'task_completed' | 'task_updated' | 'task_overdue';
  message: string;
  recipientId: ObjectId;
  recipientName: string;
  isRead: boolean;
  createdAt: Date;
  readAt?: Date;
}

export interface TaskDashboard {
  totalTasks: number;
  completedTasks: number;
  pendingTasks: number;
  overdueTasks: number;
  tasksByStage: Record<TaskStage, number>;
  tasksByPriority: Record<TaskPriority, number>;
  tasksByAssignee: Array<{
    assigneeId: ObjectId;
    assigneeName: string;
    taskCount: number;
    completedCount: number;
  }>;
  recentTasks: Task[];
  upcomingDeadlines: Task[];
}

export interface TaskFilters {
  stage?: TaskStage;
  status?: TaskStatus;
  priority?: TaskPriority;
  assignedTo?: string;
  serviceTypes?: ServiceType[]; // Updated to support multiple service types
  dateRange?: {
    start: Date;
    end: Date;
  };
  search?: string;
}

export interface TaskStats {
  totalTasks: number;
  completedTasks: number;
  pendingTasks: number;
  overdueTasks: number;
  completionRate: number;
  averageCompletionTime: number;
  tasksByStage: Record<TaskStage, {
    total: number;
    completed: number;
    pending: number;
  }>;
}
